import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Autocomplete, Box, Button, Chip, FormControlLabel, FormHelperText, IconButton,
    InputAdornment, Switch, TextField, Tooltip, Typography, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import moment from 'moment';

import Datatable from '../../../components/Datatable';
import FormDialog from '../../../components/Datatable/FormDialog';
import CustomTextField from '../../../components/CustomTextField';
import EmpresaSelect from '../../../components/EmpresaSelect';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useFormError } from '../../../components/Datatable/FormErrorProvider';
import { useAuth } from '../../../components/AuthAdminProvider';
import api, { create, get_one, update, delete_one, action } from '../../../services/api';
import DiagnoseModal from './DiagnoseModal';
import PeerScriptPanel from './PeerScriptPanel';

// Mesmo regex do backend (app/common/gre.py): gre- + [a-zA-Z0-9-]{1,11}, total 5..15 chars.
// Hífen é universal entre Mikrotik/Cisco/Huawei/Juniper. Prefixo evita colisão com builtins.
const NAME_RE = /^gre-[a-zA-Z0-9-]{1,11}$/;
const NAME_HINT = "comece com 'gre-' e use até 15 chars (letras, números e hífen). Ex: gre-cliente, gre-rml.";

function validateName(name) {
    if (!name) return 'nome obrigatório. ' + NAME_HINT;
    if (name.length > 15) return `nome tem ${name.length} chars; máximo 15. ` + NAME_HINT;
    if (!NAME_RE.test(name)) return 'inválido — ' + NAME_HINT;
    return null;
}


export default function TunnelGre() {
    const notify = useNotify();
    const auth = useAuth();
    const [formState, setFormState] = useState({ open: false, id: null });
    const [diagState, setDiagState] = useState({ open: false, id: null, name: '' });
    const [refresh, setRefresh] = useState(false);

    useEffect(() => { document.title = 'IPV7 Olimpo - Tunnel GRE'; }, []);

    const toggleEnabled = async (id, currentlyEnabled) => {
        try {
            const suffix = currentlyEnabled ? 'disable' : 'enable';
            const res = await action('/admin/tunnel', id, suffix);
            if (res.data?.data?.kernel_warning) {
                notify.setNotify({ open: true, message: `Salvo, mas: ${res.data.data.kernel_warning}`, severity: 'warning' });
            } else {
                notify.setNotify({ open: true, message: `Túnel ${currentlyEnabled ? 'desativado' : 'ativado'}.`, severity: 'success' });
            }
            setRefresh(r => !r);
        } catch (e) {
            notify.setNotify({ open: true, message: 'Falha: ' + e.message, severity: 'error' });
        }
    };

    const resetTunnel = async (id, name) => {
        if (!confirm(`Resetar o túnel ${name}? Vai derrubar e recriar.`)) return;
        try {
            const res = await action('/admin/tunnel', id, 'reset');
            if (res.data?.data?.kernel_warning) {
                notify.setNotify({ open: true, message: `Reset com aviso: ${res.data.data.kernel_warning}`, severity: 'warning' });
            } else {
                notify.setNotify({ open: true, message: 'Túnel resetado.', severity: 'success' });
            }
            setRefresh(r => !r);
        } catch (e) {
            notify.setNotify({ open: true, message: 'Falha: ' + e.message, severity: 'error' });
        }
    };

    return (
        <>
            <FormItem
                open={formState.open}
                id={formState.id}
                handlerClose={() => setFormState({ open: false, id: null })}
                handlerSubmit={() => {
                    setFormState({ open: false, id: null });
                    setRefresh(r => !r);
                }}
                btnSubmit={formState.id ? 'Atualizar' : 'Salvar'}
                btnClose={'Fechar'}
            />
            <DiagnoseModal
                open={diagState.open}
                tunnelId={diagState.id}
                tunnelName={diagState.name}
                onClose={() => setDiagState({ open: false, id: null, name: '' })}
            />
            <Datatable
                refresh={refresh}
                handlerRefresh={() => setRefresh(false)}
                title='Túneis GRE'
                endpoint='/admin/tunnel'
                options={{
                    customToolbar: () => (
                        <>
                            <Tooltip title="Recarregar (status atualizado pelo daemon)">
                                <span>
                                    <IconButton onClick={() => setRefresh(r => !r)}>
                                        <RefreshIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Adicionar">
                                <span>
                                    <IconButton
                                        disabled={!auth.hasPerm('service.create')}
                                        onClick={() => setFormState({ open: true, id: null })}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    ),
                    customToolbarSelect: () => '',
                }}
                columns={[
                    { name: 'id', label: 'ID', options: { display: false } },
                    {
                        name: 'health_status',
                        label: 'Status',
                        options: {
                            sort: false,
                            filter: false,
                            customBodyRender: (status, tableMeta) => {
                                const latency = tableMeta.rowData[10];   // health_latency_ms (index)
                                const checkedAt = tableMeta.rowData[11]; // health_checked_at
                                return <StatusDot status={status} latency={latency} checkedAt={checkedAt} />;
                            },
                        },
                    },
                    { name: 'description', label: 'Descrição' },
                    { name: 'empresa_name', label: 'Empresa' },
                    { name: 'source', label: 'Source' },
                    { name: 'destination', label: 'Destination' },
                    { name: 'ip_interface', label: 'IP /30' },
                    {
                        name: 'enabled',
                        label: 'Ativo',
                        options: {
                            customBodyRender: v => <Chip
                                size="small"
                                label={v ? 'sim' : 'não'}
                                color={v ? 'success' : 'default'}
                                variant={v ? 'filled' : 'outlined'}
                            />,
                        },
                    },
                    {
                        name: 'updated_at',
                        label: 'Atualizado em',
                        options: {
                            customBodyRender: v => <span>{v ? moment(v).locale('pt-br').format('DD-MM-YYYY HH:mm') : '-'}</span>,
                        },
                    },
                    { name: 'health_latency_ms', label: 'RTT (ms)', options: { display: false } },
                    { name: 'health_checked_at', label: 'Verificado', options: { display: false } },
                    {
                        name: 'actions',
                        label: 'Ações',
                        options: {
                            filter: false, sort: false,
                            customBodyRender: (_, tableMeta) => {
                                const id = tableMeta.rowData[0];
                                const description = tableMeta.rowData[2];
                                const enabled = !!tableMeta.rowData[7];
                                return (
                                    <Box sx={{ display: 'flex', gap: 0 }}>
                                        <Tooltip title="Diagnosticar (ping/traceroute)">
                                            <IconButton
                                                size="small"
                                                onClick={() => setDiagState({ open: true, id, name: description })}
                                            >
                                                <NetworkCheckIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={enabled ? 'Desativar' : 'Ativar'}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!auth.hasPerm('service.update')}
                                                    onClick={() => toggleEnabled(id, enabled)}
                                                >
                                                    <PowerSettingsNewIcon
                                                        fontSize="small"
                                                        color={enabled ? 'success' : 'disabled'}
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Resetar (derruba e recria)">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!auth.hasPerm('service.update') || !enabled}
                                                    onClick={() => resetTunnel(id, description)}
                                                >
                                                    <RestartAltIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!auth.hasPerm('service.update')}
                                                    onClick={() => setFormState({ open: true, id })}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                );
                            },
                        },
                    },
                ]}
            />
        </>
    );
}


function StatusDot({ status, latency, checkedAt }) {
    const when = checkedAt ? moment(checkedAt).fromNow() : 'nunca';
    const rtt = latency != null ? `${Number(latency).toFixed(2)} ms` : '';

    if (status === 'disabled') {
        return <Tooltip title="Desativado"><FiberManualRecordIcon sx={{ color: '#bbb' }} fontSize="small" /></Tooltip>;
    }
    if (status === 'up') {
        return <Tooltip title={`UP ${rtt} · verificado ${when}`}><FiberManualRecordIcon color="success" fontSize="small" /></Tooltip>;
    }
    if (status === 'down') {
        return <Tooltip title={`DOWN · verificado ${when}`}><FiberManualRecordIcon color="error" fontSize="small" /></Tooltip>;
    }
    // unknown / null
    return <Tooltip title={`Status indeterminado (${when})`}><FiberManualRecordIcon sx={{ color: '#e0e0e0' }} fontSize="small" /></Tooltip>;
}


function FormItem({ id, open, handlerClose, handlerSubmit, btnClose = 'Fechar', btnSubmit = 'Salvar' }) {
    const notify = useNotify();
    const error = useFormError();
    const auth = useAuth();
    const [isLoaded, setIsLoaded] = useState(true);
    const [sources, setSources] = useState([]);
    const [defaultSource, setDefaultSource] = useState('');
    const [ipCheck, setIpCheck] = useState({ status: 'idle', message: '' });
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [ipOptions, setIpOptions] = useState([]);
    const [ipOptionsLoading, setIpOptionsLoading] = useState(false);
    const checkTimerRef = useRef(null);
    const optionsTimerRef = useRef(null);

    const modelState = {
        description: '',
        source: '',
        destination: '',
        ip_interface: '',
        enabled: true,
        empresa: null,
    };
    const [state, setState] = useState(modelState);

    // Carrega sources existentes (uma vez ao abrir)
    useEffect(() => {
        if (!open) return;
        api.get('/admin/tunnel/sources').then(res => {
            const data = res.data?.data || {};
            setSources(data.sources || []);
            setDefaultSource(data.default || '');
        }).catch(() => { /* tela funciona sem isso */ });
    }, [open]);

    useEffect(() => {
        setState(modelState);
        setIpCheck({ status: 'idle', message: '' });
        if (!open) return;
        error.setErrors([]);
        if (id) {
            setIsLoaded(false);
            get_one('/admin/tunnel', id).then(res => {
                setState({
                    description: res.data.data.description ?? '',
                    source: res.data.data.source ?? '',
                    destination: res.data.data.destination ?? '',
                    ip_interface: res.data.data.ip_interface ?? '',
                    enabled: !!res.data.data.enabled,
                    empresa: res.data.data.empresa ?? null,
                });
                setIsLoaded(true);
            }).catch(e => {
                console.log(e);
                setIsLoaded(true);
            });
        } else {
            // Pre-fill source com o default ao abrir form de criar
            setState(s => ({ ...s, source: defaultSource || '' }));
        }
    }, [id, open, defaultSource]);

    const handleChange = (event) => {
        setState({ ...state, [event.target.name]: event.target.value });
    };

    // Check de ip_interface com debounce 500ms
    const triggerIpCheck = (value) => {
        clearTimeout(checkTimerRef.current);
        if (!value || value.length < 5) {
            setIpCheck({ status: 'idle', message: '' });
            return;
        }
        checkTimerRef.current = setTimeout(async () => {
            setIpCheck({ status: 'loading', message: '' });
            try {
                const res = await api.post('/admin/tunnel/check_ip_interface',
                    { ip_interface: value, exclude_id: id ?? null });
                const d = res.data?.data || {};
                if (!d.valid) {
                    setIpCheck({ status: 'invalid', message: d.error || 'inválido' });
                } else if (!d.available) {
                    setIpCheck({
                        status: 'conflict',
                        message: `colide com ${d.conflict?.description} (id ${d.conflict?.id})`,
                    });
                } else {
                    setIpCheck({
                        status: 'ok',
                        message: `disponível — peer ${d.peer}`,
                    });
                }
            } catch (e) {
                setIpCheck({ status: 'idle', message: '' });
            }
        }, 500);
    };

    // Autocomplete: ao digitar prefix tipo "10.48", busca 10 /30s livres com esse prefix.
    const triggerOptions = (value) => {
        clearTimeout(optionsTimerRef.current);
        // Não dispara se já tem máscara completa (formato pronto)
        if (!value || /\/\d+$/.test(value)) {
            setIpOptions([]);
            return;
        }
        // Só faz sentido com pelo menos 1 octeto + ponto (ex: "10.")
        if (!/^\d{1,3}(\.\d{0,3}){0,3}$/.test(value)) {
            setIpOptions([]);
            return;
        }
        const parts = value.split('.').filter(Boolean);
        if (parts.length < 1 || parts.length > 3) {
            setIpOptions([]);
            return;
        }
        optionsTimerRef.current = setTimeout(async () => {
            setIpOptionsLoading(true);
            try {
                const res = await api.get('/admin/tunnel/suggest_ip_interface', {
                    params: { prefix: value, count: 10 }
                });
                const sugs = res.data?.data?.suggestions || [];
                setIpOptions(sugs.map(s => ({
                    label: s.ip_interface,
                    value: s.ip_interface,
                    peer: s.peer,
                    network: s.network,
                })));
            } catch (e) {
                setIpOptions([]);
            } finally { setIpOptionsLoading(false); }
        }, 400);
    };

    const handleIpChange = (newValue) => {
        const v = typeof newValue === 'string' ? newValue : newValue?.value || '';
        setState(s => ({ ...s, ip_interface: v }));
        triggerIpCheck(v);
        triggerOptions(v);
    };

    const handleSuggest = async () => {
        setSuggestLoading(true);
        try {
            // count=1 → backend usa random, rotação natural a cada clique
            const res = await api.get('/admin/tunnel/suggest_ip_interface', { params: { count: 1 } });
            const sug = res.data?.data?.suggestions?.[0];
            if (sug) {
                setState(s => ({ ...s, ip_interface: sug.ip_interface }));
                setIpCheck({ status: 'ok', message: `sugerido — peer ${sug.peer}` });
                setIpOptions([]);
            } else if (res.data?.error) {
                notify.setNotify({ open: true, message: res.data.error, severity: 'error' });
            }
        } catch (e) {
            notify.setNotify({ open: true, message: 'Falha ao sugerir IP: ' + e.message, severity: 'error' });
        } finally { setSuggestLoading(false); }
    };

    const handleResponse = (res, successMsg) => {
        if (res.data?.error) {
            error.setErrors([{ field: 'global', msg: res.data.error }]);
            notify.setNotify({ open: true, message: res.data.error, severity: 'error' });
            return;
        }
        if (res.data?.errors || res.data?.data?.errors) {
            error.setErrors(res.data.errors || res.data.data.errors);
            return;
        }
        if (res.data?.data?.kernel_warning) {
            notify.setNotify({
                open: true,
                message: `Salvo. Aviso do kernel: ${res.data.data.kernel_warning}. Reconciler vai ajustar.`,
                severity: 'warning',
            });
        } else {
            notify.setNotify({ open: true, message: successMsg, severity: 'success' });
        }
        handlerSubmit();
        error.setErrors([]);
    };

    const wrapperSubmit = () => {
        const payload = {
            description: state.description,
            source: state.source,
            destination: state.destination,
            ip_interface: state.ip_interface,
            enabled: !!state.enabled,
            empresa: state.empresa,
        };
        const onErr = e => notify.setNotify({ open: true, message: e.message, severity: 'error' });
        if (id) {
            update('/admin/tunnel', id, payload)
                .then(res => handleResponse(res, 'Túnel atualizado com sucesso.'))
                .catch(onErr);
        } else {
            create('/admin/tunnel/create', payload)
                .then(res => handleResponse(res, 'Túnel criado com sucesso.'))
                .catch(onErr);
        }
    };

    const ipHelperText = ipCheck.message || error.msgError('ip_interface') || 'IP/máscara da nossa ponta do /30, ex: 10.0.0.1/30';
    const ipColor = {
        ok: 'success.main', conflict: 'error.main', invalid: 'error.main',
        loading: 'text.secondary', idle: 'text.secondary',
    }[ipCheck.status];
    const ipIcon = {
        ok: <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />,
        conflict: <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />,
        invalid: <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />,
        loading: <CircularProgress size={16} />,
        idle: null,
    }[ipCheck.status];

    return (
        <FormDialog
            isLoaded={isLoaded}
            title={
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" component="div">{id ? 'Editar' : 'Adicionar'} Túnel GRE</Typography>
                    {id && (
                        <IconButton
                            disabled={!auth.hasPerm('service.delete')}
                            onClick={() => {
                                if (confirm('Deseja excluir o túnel?')) {
                                    delete_one('/admin/tunnel', id).then(() => {
                                        notify.setNotify({ open: true, message: 'Túnel deletado.', severity: 'success' });
                                        handlerClose();
                                        handlerSubmit();
                                    });
                                }
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>
            }
            open={open}
            handlerSubmit={wrapperSubmit}
            handlerClose={handlerClose}
            btnSubmit={btnSubmit}
            btnClose={btnClose}
            maxWidth="md"
        >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 1 }}>
                {/* Form esquerda */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                    <EmpresaSelect
                        disabled={!!id}
                        value={state.empresa}
                        handler={(e) => setState({ ...state, empresa: e })}
                    />
                    {(() => {
                        const localErr = state.description ? validateName(state.description) : null;
                        const helper = localErr || error.msgError('description') || NAME_HINT;
                        const isErr = !!localErr || error.verify('description');
                        return (
                            <CustomTextField
                                error={isErr}
                                helperText={helper}
                                label='Descrição (nome da interface)'
                                value={state.description}
                                name='description'
                                onChange={handleChange}
                                inputProps={{ maxLength: 15 }}
                                placeholder='gre-cliente'
                            />
                        );
                    })()}
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={sources}
                        value={state.source}
                        onChange={(_, v) => setState(s => ({ ...s, source: v || '' }))}
                        onInputChange={(_, v) => setState(s => ({ ...s, source: v || '' }))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Source (IP local)"
                                error={error.verify('source')}
                                helperText={error.msgError('source') || 'IP de saída na VM Anubis (sugestões já preenchidas)'}
                            />
                        )}
                    />
                    <CustomTextField
                        error={error.verify('destination')}
                        helperText={error.msgError('destination') || 'IP público do peer remoto'}
                        label='Destination (IP remoto)'
                        value={state.destination}
                        name='destination'
                        onChange={handleChange}
                        placeholder='190.0.0.1'
                    />
                    <Box>
                        <Autocomplete
                            freeSolo
                            size="small"
                            value={state.ip_interface}
                            inputValue={state.ip_interface}
                            options={ipOptions}
                            loading={ipOptionsLoading}
                            filterOptions={(x) => x}
                            getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
                            isOptionEqualToValue={(opt, val) =>
                                (typeof opt === 'string' ? opt : opt.value) === val}
                            onInputChange={(_, v) => handleIpChange(v)}
                            onChange={(_, v) => handleIpChange(v)}
                            renderOption={(props, opt) => (
                                <li {...props} key={opt.value}>
                                    <Box>
                                        <Typography variant="body2">{opt.label}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            peer {opt.peer} · rede {opt.network}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='IP da interface (/30)'
                                    placeholder='digite tipo "10.48" pra ver opções livres'
                                    error={error.verify('ip_interface') || ipCheck.status === 'conflict' || ipCheck.status === 'invalid'}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {ipIcon}
                                                <Tooltip title="Sugerir /30 livre (rotaciona a cada clique)">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleSuggest}
                                                            disabled={suggestLoading}
                                                        >
                                                            {suggestLoading ? <CircularProgress size={16} /> : <AutoFixHighIcon fontSize="small" />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <FormHelperText sx={{ color: ipColor, ml: 1.5 }}>{ipHelperText}</FormHelperText>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!state.enabled}
                                onChange={(e) => setState({ ...state, enabled: e.target.checked })}
                                color="success"
                            />
                        }
                        label={state.enabled ? 'Ativo' : 'Desativado'}
                    />
                </Box>

                {/* Painel script peer direita */}
                <Box sx={{ flex: 1, minWidth: { md: 380 } }}>
                    <PeerScriptPanel
                        source={state.source}
                        destination={state.destination}
                        ipInterface={state.ip_interface}
                        name={state.description}
                    />
                </Box>
            </Box>
        </FormDialog>
    );
}
